import React from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Divider,
  Stack,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  BusinessOutlined as OrganizationIcon,
  PersonOutlineOutlined as PersonIcon,
  PhoneOutlined as PhoneIcon,
  BadgeOutlined as BadgeIcon,
} from '@mui/icons-material';

interface DataSharingConsentFormProps {
  organisationName: string;
  onConsent: () => void;
  onDecline: () => void;
}

const DataSharingConsentForm: React.FC<DataSharingConsentFormProps> = ({
  organisationName,
  onConsent,
  onDecline,
}) => {
  return (
    <Card elevation={3} sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Box textAlign="center" mb={3}>
          <OrganizationIcon
            sx={{ fontSize: 60, color: 'primary.main', mb: 2 }}
          />
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            data-testid="consent-page-title"
          >
            Data Sharing Consent
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Share your profile with {organisationName}?
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Alert severity="info" sx={{ mb: 3 }}>
          You&apos;re already registered with our platform. To join{' '}
          {organisationName}, we need your permission to share your profile
          information with them.
        </Alert>

        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          Information to be shared:
        </Typography>

        <List sx={{ mb: 3 }}>
          <ListItem>
            <ListItemIcon>
              <PersonIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Name" secondary="Your first and last name" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <PhoneIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Contact Information"
              secondary="Your phone number and email address"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <BadgeIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Business Information"
              secondary="Your CIS and business details (if provided)"
            />
          </ListItem>
        </List>

        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
          Your information will only be shared with {organisationName} and will
          be used in accordance with their privacy policy.
        </Alert>

        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            onClick={onDecline}
            data-testid="consent-decline-button"
          >
            Decline
          </Button>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={onConsent}
            data-testid="consent-accept-button"
          >
            Yes, Share My Information
          </Button>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 3, textAlign: 'center' }}
        >
          You can manage your data sharing preferences at any time from your
          account settings.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default DataSharingConsentForm;
